import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gavel, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { peso } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REGIONS = ["NCR", "CAR", "I", "II", "III", "IV-A", "MIMAROPA", "V", "VI", "VII",
  "VIII", "IX", "X", "XI", "XII", "XIII", "BARMM"];
const EMPTY = {
  region: "NCR", order_no: "", effective_date: "", minimum_daily_wage: "",
  cola_daily: "", sector: "non-agriculture", status: "effective", notes: "",
};

/**
 * DOLE wage orders, and what each one does to the contracts on file.
 *
 * ⚠️ DO 150-16 requires every service agreement to carry an automatic crediting
 * provision: when DOLE issues a wage order the PRINCIPAL shoulders the increase
 * and the agreement is deemed amended. That clause is in nearly every security
 * contract in the country and almost nowhere an operable step — so the increase
 * reaches the guard months late, or not at all, and the agency absorbs a
 * shortfall it never agreed to.
 *
 * ⚠️ AN ORDER CAN BE ISSUED AND NEVER TAKE EFFECT. NCR-27 was stopped by the
 * Pasig court and NCR-28's own preamble records that it "never took effect". An
 * enjoined order stays on file — somebody will ask why the numbering jumps — and
 * is never applied to anything.
 */
export default function WageOrders() {
  const [rows, setRows] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/wage-orders").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(load, []);

  const create = async () => {
    try {
      await api.post("/wage-orders", {
        ...form,
        minimum_daily_wage: Number(form.minimum_daily_wage) || 0,
        cola_daily: Number(form.cola_daily) || 0,
      });
      toast.success("Wage order recorded");
      setNewOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const enjoin = async (r) => {
    try {
      await api.patch(`/wage-orders/${r.id}`, { status: r.enjoined ? "effective" : "enjoined" });
      toast.success(r.enjoined ? `${r.order_no} is in force again` : `${r.order_no} marked enjoined — it will not be applied`);
      load();
      setImpact(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const showImpact = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/wage-orders/${r.id}/impact`);
      setImpact(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const columns = [
    { key: "order_no", label: "Order", render: (r) => (
      <div>
        <div className="font-semibold">{r.order_no}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{r.ref_no} · {r.region}</div>
      </div>
    ) },
    { key: "effective_date", label: "Effective", render: (r) => <span className="font-mono text-xs">{r.effective_date}</span> },
    { key: "minimum_daily_wage", label: "Minimum / day", render: (r) => (
      <span className="font-mono text-sm font-bold">{peso(r.minimum_daily_wage + (r.cola_daily || 0))}</span>
    ) },
    { key: "sector", label: "Sector" },
    { key: "state", label: "State", render: (r) => (
      r.enjoined
        ? <span className="inline-block border border-[hsl(var(--alert))] text-[hsl(var(--alert))] px-2 py-0.5 font-mono text-[10px] uppercase">enjoined</span>
        : r.in_force
          ? <span className="inline-block border border-[hsl(var(--success))] text-[hsl(var(--success))] px-2 py-0.5 font-mono text-[10px] uppercase">in force</span>
          : r.future
            ? <span className="inline-block border border-[hsl(var(--warning))] text-[hsl(var(--warning))] px-2 py-0.5 font-mono text-[10px] uppercase">takes effect soon</span>
            : <span className="inline-block border border-border text-muted-foreground px-2 py-0.5 font-mono text-[10px] uppercase">superseded</span>
    ) },
    { key: "notes", label: "Notes", render: (r) => <span className="text-[11px] text-muted-foreground">{r.notes}</span> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" disabled={busy || r.enjoined}
                data-testid={`impact-${r.order_no}`} onClick={() => showImpact(r)}>
          What it costs
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none h-7 text-[11px]"
                data-testid={`enjoin-${r.order_no}`} onClick={() => enjoin(r)}>
          {r.enjoined ? "Lift" : "Mark enjoined"}
        </Button>
      </div>
    ) },
  ];

  return (
    <div data-testid="page-wage-orders">
      <PageHeader
        title="Wage orders"
        subtitle="What DOLE has set, per region — and which contracts fall below it"
        action={<Button className="rounded-none" data-testid="new-wage-order" onClick={() => setNewOpen(true)}>Record a wage order</Button>}
      />

      <div className="border border-border bg-card p-3 mb-4 text-xs text-muted-foreground leading-relaxed max-w-4xl">
        <div className="flex items-center gap-2 font-heading font-bold text-foreground text-sm mb-1">
          <Gavel size={16} /> Why this screen exists
        </div>
        Every service agreement carries an <span className="text-foreground">automatic crediting provision</span>:
        when DOLE issues a wage order, the principal shoulders the increase and the agreement is deemed amended
        (DO 150-16). Until somebody can list <em>which</em> agreements and by how much, that clause is a sentence
        nobody acts on — the increase reaches the guard late, and the agency absorbs a shortfall it never agreed
        to. The minimum is <span className="text-foreground">regional and follows the post</span>, so a site with
        no region on it cannot be checked at all.
      </div>

      <DataTable columns={columns} rows={rows} testid="wage-orders-table"
                 empty="No wage orders on file. Without one, no contract's pay rate can be checked against the law." />

      {impact && (
        <div className="border border-border bg-card p-4 mt-5" data-testid="wage-impact">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading font-bold text-lg">
                {impact.wage_order.order_no} — {peso(impact.floor_daily)}/day from {impact.wage_order.effective_date}
              </div>
              <div className="text-xs text-muted-foreground">
                {impact.sites_in_region} site(s) in {impact.wage_order.region} · {impact.guards_on_those_sites} guard(s) standing on them
              </div>
            </div>
            <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setImpact(null)}>Close</Button>
          </div>

          {impact.contracts_needing_escalation.length === 0 ? (
            <div className="border border-[hsl(var(--success))] p-3 text-xs">
              Every live contract in this region already pays at or above this order and bills enough to cover it.
            </div>
          ) : (
            <>
              <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 mb-3 flex gap-2">
                <Warning size={16} weight="fill" className="text-[hsl(var(--alert))] shrink-0 mt-0.5" />
                <div className="text-xs leading-snug">
                  <span className="font-bold">{impact.contracts_needing_escalation.length} contract(s) must be escalated.</span>{" "}
                  Until they are, the agency is paying below the legal minimum or billing below what it must pay —
                  and the principal is jointly and severally liable for the shortfall.
                </div>
              </div>
              {impact.contracts_needing_escalation.map((r) => (
                <div key={r.contract_id} className="border-t border-border py-2" data-testid={`impact-row-${r.ref_no}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-sm">{r.client_name} <span className="font-mono text-[10px] text-muted-foreground">{r.ref_no}</span></span>
                    <span className="font-mono text-xs">
                      pay {peso(r.current_pay_rate_daily)} → {peso(r.new_minimum)}/day
                      {r.shortfall_daily > 0 && <span className="text-[hsl(var(--alert))] font-bold"> (short {peso(r.shortfall_daily)})</span>}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.sites.join(", ")} · billing {peso(r.billed_monthly_per_guard)}/month per guard against a
                    computed {peso(r.new_total_monthly_per_guard)}
                    {r.bill_shortfall_monthly > 0 && (
                      <span className="text-[hsl(var(--alert))] font-semibold"> — short {peso(r.bill_shortfall_monthly)} per guard per month</span>
                    )}
                  </div>
                </div>
              ))}
              <div className="text-[10px] text-muted-foreground mt-3 leading-snug">
                Escalate each one from <span className="text-foreground">Clients &amp; Contracts</span>. Nothing is
                changed from here: crediting a wage order to a client is a conversation with that client, and a
                screen that did it on their behalf would be making the decision for them.
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none max-w-xl">
          <DialogHeader><DialogTitle className="font-heading">Record a DOLE wage order</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase font-bold">Region</Label>
              <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                <SelectTrigger data-testid="wo-region" className="rounded-none mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Fld label="Order number" v={form.order_no} set={(v) => setForm({ ...form, order_no: v })} ph="NCR-28" testid="wo-no" />
            <div>
              <Label className="text-[10px] uppercase font-bold">Effective from</Label>
              <Input type="date" data-testid="wo-effective" className="rounded-none mt-1 h-9 font-mono"
                     value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
            </div>
            <Fld label="Minimum daily wage" v={form.minimum_daily_wage} set={(v) => setForm({ ...form, minimum_daily_wage: v })} ph="755" testid="wo-min" mono />
            <Fld label="COLA / day" v={form.cola_daily} set={(v) => setForm({ ...form, cola_daily: v })} ph="0" testid="wo-cola" mono />
            <Fld label="Sector" v={form.sector} set={(v) => setForm({ ...form, sector: v })} testid="wo-sector" />
            <div className="md:col-span-2">
              <Fld label="Notes" v={form.notes} set={(v) => setForm({ ...form, notes: v })}
                   ph="Anything that explains this order to somebody reading the file later" testid="wo-notes" />
            </div>
          </div>
          <Button className="w-full rounded-none h-11 font-heading font-bold" data-testid="wo-submit"
                  disabled={!form.order_no || !form.effective_date || !form.minimum_daily_wage}
                  onClick={create}>
            RECORD IT
          </Button>
          <div className="text-[10px] text-muted-foreground text-center leading-snug">
            If a court later stops this order, mark it <span className="text-foreground">enjoined</span> rather
            than deleting it — the order existed, people planned against it, and the next order will refer back to it.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, v, set, ph, testid, mono }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold">{label}</Label>
      <Input data-testid={testid} className={"rounded-none mt-1 h-9 " + (mono ? "font-mono" : "")}
             placeholder={ph} value={v} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
